import { useEffect, useState } from "preact/hooks";
import { api, type Rule, type RuleCondition } from "../api";
import { SYSTEM_NAMES } from "../format";

const FIELD_NAMES: Record<RuleCondition["field"], string> = { from: "Expéditeur", to: "Destinataire", subject: "Objet" };
const OP_NAMES: Record<RuleCondition["op"], string> = { contains: "contient", is: "est" };

type Editing = Omit<Rule, "id"> & { id?: string; applyNow: boolean };

const blank = (): Editing => ({
  name: "", enabled: true, conditions: [{ field: "from", op: "contains", value: "" }], actions: {}, applyNow: true,
});

function summary(rule: Rule, folderName: (id: string) => string): string {
  const when = rule.conditions.map((c) => `${FIELD_NAMES[c.field].toLowerCase()} ${OP_NAMES[c.op]} « ${c.value} »`).join(" et ");
  const then = [
    rule.actions.moveTo && `ranger dans ${folderName(rule.actions.moveTo)}`,
    rule.actions.markRead && "marquer comme lu",
    rule.actions.star && "ajouter une étoile",
  ].filter(Boolean).join(", ");
  return `Si ${when} : ${then || "rien"}`;
}

// Règles automatiques (exigence C2), dans les réglages.
export function Rules({ notify }: { notify: (text: string) => void }) {
  const [rules, setRules] = useState<Rule[]>([]);
  const [folders, setFolders] = useState<{ id: string; name: string }[]>([]);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () => api.rules().then((r) => setRules(r.rules));
  useEffect(() => {
    load().catch(() => {});
    api.folders().then((f) => setFolders(f.custom)).catch(() => {});
  }, []);

  const targets = [
    ...folders,
    ...(["archive", "spam", "trash"] as const).map((id) => ({ id, name: SYSTEM_NAMES[id] })),
  ];
  const folderName = (id: string) => targets.find((t) => t.id === id)?.name ?? "un dossier supprimé";

  const save = async () => {
    if (!editing) return;
    setError(null);
    const { applyNow, id, ...rule } = editing;
    try {
      const saved = id ? await api.updateRule(id, rule) : await api.createRule(rule);
      if (applyNow) {
        const { updated } = await api.applyRule(saved.id);
        notify(updated ? `Règle appliquée à ${updated} e-mail${updated > 1 ? "s" : ""} de la boîte de réception` : "Règle enregistrée");
      } else {
        notify("Règle enregistrée");
      }
      setEditing(null);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const setCondition = (i: number, patch: Partial<RuleCondition>) =>
    setEditing({ ...editing!, conditions: editing!.conditions.map((c, j) => (j === i ? { ...c, ...patch } : c)) });

  return (
    <>
      <h2>Règles</h2>
      <p class="sub">Appliquées à chaque e-mail reçu, dans l'ordre de la liste.</p>
      <ul>
        {rules.map((r) => (
          <li>
            <input type="checkbox" checked={r.enabled} aria-label={r.enabled ? "Désactiver" : "Activer"}
              onChange={async () => { await api.updateRule(r.id, { ...r, enabled: !r.enabled }).catch((e) => notify((e as Error).message)); load(); }} />
            <div class="grow">
              <div>{r.name || "Règle sans nom"}</div>
              <div class="sub">{summary(r, folderName)}</div>
            </div>
            <button class="btn" onClick={() => setEditing({ ...r, applyNow: false })}>Modifier</button>
            <button class="btn danger" onClick={async () => {
              if (!confirm("Supprimer cette règle ?")) return;
              await api.deleteRule(r.id).catch((e) => notify((e as Error).message));
              load();
            }}>Supprimer</button>
          </li>
        ))}
      </ul>

      {editing ? (
        <div class="rule-form">
          <label class="setting">
            <span>Nom (facultatif)</span>
            <input value={editing.name} onInput={(e) => setEditing({ ...editing, name: (e.target as HTMLInputElement).value })} />
          </label>
          <div class="setting">
            <span>Si</span>
            {editing.conditions.map((c, i) => (
              <div class="rule-row">
                <select value={c.field} aria-label="Champ" onChange={(e) => setCondition(i, { field: (e.target as HTMLSelectElement).value as RuleCondition["field"] })}>
                  {Object.entries(FIELD_NAMES).map(([v, n]) => <option value={v}>{n}</option>)}
                </select>
                <select value={c.op} aria-label="Comparaison" onChange={(e) => setCondition(i, { op: (e.target as HTMLSelectElement).value as RuleCondition["op"] })}>
                  {Object.entries(OP_NAMES).map(([v, n]) => <option value={v}>{n}</option>)}
                </select>
                <input value={c.value} aria-label="Valeur" autocapitalize="none" placeholder={c.field === "subject" ? "facture" : "amazon.fr"}
                  onInput={(e) => setCondition(i, { value: (e.target as HTMLInputElement).value })} />
                {editing.conditions.length > 1 && (
                  <button class="icon-btn" aria-label="Retirer la condition"
                    onClick={() => setEditing({ ...editing, conditions: editing.conditions.filter((_, j) => j !== i) })}>×</button>
                )}
              </div>
            ))}
            {editing.conditions.length < 5 && (
              <button class="link-btn" onClick={() => setEditing({ ...editing, conditions: [...editing.conditions, { field: "subject", op: "contains", value: "" }] })}>
                + Ajouter une condition (toutes doivent être vraies)
              </button>
            )}
          </div>
          <div class="setting">
            <span>Alors</span>
            <select value={editing.actions.moveTo ?? ""} aria-label="Ranger dans"
              onChange={(e) => setEditing({ ...editing, actions: { ...editing.actions, moveTo: (e.target as HTMLSelectElement).value || undefined } })}>
              <option value="">Laisser dans la boîte de réception</option>
              {targets.map((t) => <option value={t.id}>Ranger dans {t.name}</option>)}
            </select>
            <label class="check"><input type="checkbox" checked={!!editing.actions.markRead}
              onChange={(e) => setEditing({ ...editing, actions: { ...editing.actions, markRead: (e.target as HTMLInputElement).checked || undefined } })} />
              Marquer comme lu</label>
            <label class="check"><input type="checkbox" checked={!!editing.actions.star}
              onChange={(e) => setEditing({ ...editing, actions: { ...editing.actions, star: (e.target as HTMLInputElement).checked || undefined } })} />
              Ajouter une étoile</label>
          </div>
          <label class="check"><input type="checkbox" checked={editing.applyNow}
            onChange={(e) => setEditing({ ...editing, applyNow: (e.target as HTMLInputElement).checked })} />
            Appliquer aussi aux e-mails déjà dans la boîte de réception</label>
          {error && <p class="error" role="alert">{error}</p>}
          <p class="rule-buttons">
            <button class="btn primary" onClick={save}>Enregistrer</button>
            <button class="btn" onClick={() => { setEditing(null); setError(null); }}>Annuler</button>
          </p>
        </div>
      ) : (
        <p><button class="btn" onClick={() => setEditing(blank())}>Nouvelle règle</button></p>
      )}
    </>
  );
}
